import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFlashState from 'hooks/useFlashState';
import BasicModal from 'components/Modals/presets/BasicModal';
import Automation2Service from 'services/Automation2Service';
import { getOriginalError } from 'services/AxiosService';
import { KeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import { useKeyListeners } from 'hooks/keyboardEventHandler/useKeyListeners';
import { FIELD_TYPES } from 'utility/constants';
import { usePreserveCaretPosition } from 'hooks/usePreserveCaretPosition';
import { MAX_AUTOMATION_NAME_LENGTH } from './constants';
import { FolderSelect } from './EditFolderModal/FolderSelect';
import { useSelectScrollIntoView } from './EditFolderModal/hooks/useSelectScroll';
import { TypeChooser } from './TypeChooser';
import { StyledModalSelect, StyledModalTextInput } from './styles';

const NewAutomationModal = ({
  currentDirectory,
  folders,
  models,
  onHide,
  handleCreate,
  onAddFolder,
  show,
  validParentFolders,
  ...others
}) => {
  const { t } = useTranslation();

  const [automationName, setAutomationName] = useState('');
  const [automationEmail, setAutomationEmail] = useState('');
  const [chosenModel, setModel] = useState(null);
  const [automationType, setAutomationType] = useState('record_based');
  const [folder, setFolder] = useState(() => {
    if (!currentDirectory.parent_folder_id) {
      return null;
    }
    return folders.find((x) => x.id === currentDirectory.id) ?? null;
  });
  const [errors, setErrors] = useFlashState(1500, {});
  const history = useHistory();

  useEffect(() => {
    if (show) {
      setAutomationName('');
      setAutomationEmail('');
      setModel(null);
      setAutomationType('record_based');
    }
  }, [show, setErrors]);

  const handleClick = async () => {
    try {
      const data = {
        name: automationName.trim(),
        ...(automationType === 'record_based'
          ? { customObjectId: chosenModel }
          : null),
        errorNotificationEmail: automationEmail || null,
        type: automationType,
      };

      if (folder) {
        data.folderId = folder.id;
      }

      const automation = await Automation2Service.create(data);
      history.push(`/agentic-workflow/${automation.id}/`);
      handleCreate();
    } catch (error) {
      const originalError = getOriginalError(error);
      if (originalError) {
        setErrors(originalError);
      }
    }
  };

  const keyHandlers = useMemo(
    () =>
      [
        { id: 'automation' },
        { id: 'automation-type' },
        automationType === 'record_based'
          ? {
              id: 'automation-object',
            }
          : null,
        {
          id: 'automation-email',
        },
      ].filter(Boolean),
    [automationType]
  );

  const { assignFieldHandle, getKeyListenersProps } = useKeyListeners(
    keyHandlers,
    { canFocusField: () => true }
  );

  const inputRef = usePreserveCaretPosition(automationName);
  const selectScrollProps = useSelectScrollIntoView();

  return (
    <BasicModal
      onHide={onHide}
      show={show}
      heading={t('Add Agentic Workflow')}
      disabled={
        !automationName.trim() ||
        (automationType === 'record_based' && !chosenModel)
      }
      onConfirm={handleClick}
      buttonText={t('Save')}
      defaultLeftBtnText={t('Cancel')}
      {...others}
    >
      <KeyBoardContext.Provider value={{ assignFieldHandle }}>
        <StyledModalTextInput
          ref={(el) => inputRef(el && el.querySelector('input'))}
          label={t('Agentic Workflow Name')}
          placeholder={t('Enter Name')}
          value={automationName}
          onChange={(v) =>
            setAutomationName(v.slice(0, MAX_AUTOMATION_NAME_LENGTH))
          }
          validate={
            errors.name && {
              message: errors.name,
              showMessage: true,
            }
          }
          inModal
          {...getKeyListenersProps('automation')}
          data-qa="automation-name"
        />
        <TypeChooser
          automationType={automationType}
          setAutomationType={setAutomationType}
          typeKeyboardProps={getKeyListenersProps('automation-type')}
        />
        {automationType === 'record_based' ? (
          <StyledModalSelect
            label={t('Choose Object')}
            placeholder={t('Choose Object')}
            options={models}
            value={chosenModel}
            onChange={setModel}
            menuInline
            menuLeftButton={null}
            menuRightButton={null}
            field={{ fieldType: FIELD_TYPES.Dropdown, options: models }}
            {...getKeyListenersProps('automation-object')}
            {...selectScrollProps}
            data-qa="automation-object"
          />
        ) : null}

        <StyledModalTextInput
          label={t('Additional Error Notification Email')}
          labelInfo={t(
            'Error emails will always be sent to the creator by default, but you may also add another email recipient (such as a distribution group)'
          )}
          labelInfoPlacement="top"
          labelInfoMaxWidth={255}
          placeholder={t('Email')}
          value={automationEmail}
          onChange={setAutomationEmail}
          inModal
          validate={
            errors.error_notification_email && {
              message: errors.error_notification_email,
              showMessage: true,
            }
          }
          {...getKeyListenersProps('automation-email')}
          data-qa="automation-email"
        />
        <FolderSelect
          chosenFolder={folder}
          folders={folders}
          validParentFolders={validParentFolders}
          onChange={setFolder}
          onAddFolder={onAddFolder}
          error={errors.folder_id}
          currentDirectory={currentDirectory}
        />
      </KeyBoardContext.Provider>
    </BasicModal>
  );
};

export default NewAutomationModal;
